const SESSION_STORAGE_KEY = "nav-health-session-id";

function getSessionId(): string {
  if (typeof window === "undefined") {
    return "server";
  }

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const generated =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  window.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
  return generated;
}

function toErrorMessage(value: unknown): string {
  if (value instanceof Error) {
    return value.message;
  }
  return String(value ?? "");
}

function isIgnorableRscAbort(value: unknown): boolean {
  const message = toErrorMessage(value).toLowerCase();
  const isAbort = message.includes("abort") || message.includes("aborted") || message.includes("aborterror");
  const isRscContext = message.includes("_rsc") || message.includes("prefetch");
  return isAbort && isRscContext;
}

async function postTelemetry(eventType: string, metadata: Record<string, string>) {
  try {
    await fetch("/api/education/telemetry", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      keepalive: true,
      body: JSON.stringify({
        eventType,
        occurredAtUtc: new Date().toISOString(),
        metadata,
      }),
    });
  } catch {
    // Best-effort telemetry only.
  }
}

export function trackNavigationRendered(url: string) {
  if (typeof window === "undefined") {
    return;
  }

  void postTelemetry("app_navigation_rendered", {
    sessionId: getSessionId(),
    url,
  });
}

export function registerNavigationHealthErrorHandlers() {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isIgnorableRscAbort(event.reason)) {
      return;
    }

    void postTelemetry("app_client_unhandled_rejection", {
      sessionId: getSessionId(),
      url: window.location.href,
      message: toErrorMessage(event.reason).slice(0, 500),
    });
  };

  const onError = (event: ErrorEvent) => {
    const combined = `${event.message || ""} ${event.filename || ""}`;
    if (isIgnorableRscAbort(combined) || isIgnorableRscAbort(event.error)) {
      return;
    }

    void postTelemetry("app_client_error", {
      sessionId: getSessionId(),
      url: window.location.href,
      message: toErrorMessage(event.error || event.message).slice(0, 500),
    });
  };

  window.addEventListener("unhandledrejection", onUnhandledRejection);
  window.addEventListener("error", onError);

  return () => {
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    window.removeEventListener("error", onError);
  };
}